import { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Check, ChevronLeft, ChevronRight, Clock, Lock, LogOut, Mail, Settings, PenLine,
  BookMarked, CalendarDays, FileCheck,
} from '../../icons';
import { MyStatusAvatar } from '../shared/Avatar';
import { statusLabel, ICON_BY_STATUS } from '../StatusDropdown';
import { useModalLeave } from '../../hooks/useModalLeave';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { useAuthStore } from '../../store/authStore';
import { useProfileUiStore } from '../../store/profileUiStore';
import { useWorkStatusStore } from '../../store/workStatusStore';
import { useScreenLockStore } from '../../store/screenLockStore';
import { WORK_STATUS_KEYS, WORK_STATUS_TONE, isServerBacked, isCheckOutLocked } from '../../lib/work-status';
import { getMyProfile } from '../../api/users';
// 소속·사번 — 범위를 아는 라우트가 답한다(본인은 SELF 라 전부 오고, **기록도 안 남는다**).
import { fetchPersonCard } from '../../api/hr-people';
import { getLeaveSummary } from '../../api/hr';
import { leaveAvailable } from '../../lib/leave-balance';
// 「내 것」 줄이 가리키는 자리 — 주소를 여기서 짓지 않고 메뉴가 쓰는 상수를 그대로 쓴다.
import { HR_HOME_PATH } from '../../lib/hr-menu';
import { ATT_HOME_PATH } from '../../lib/att-menu';
// 회사가 끈 메뉴로는 길을 내지 않는다 — 눌러도 404(MENU_DISABLED)가 뜬다.
import { useMenuConfig, isMenuVisible } from '../../lib/menu-config';
import { logout } from '../../lib/logout';
import { tLayout, getLayoutLang, subscribeLayoutLang } from '../../lib/layout-i18n';
import './ProfilePopover.css';
import { registerSessionCache } from '../../lib/session-caches';

/**
 * ⑤ 레일 프로필 팝오버 — 2026-08-12 사용자 요구.
 *
 *   "프로필을 클릭하면 나의 프로필 사진과 이름, 직책, 이메일주소, 근태설정 아이콘,
 *    설정아이콘, 로그아웃 아이콘 / 근태설정 > 출근, 퇴근, 출장 등으로 수정"
 *
 * 종전 레일 아바타는 `/me` 로 바로 이동할 뿐이라, 근무 상태(하단 상태바)·로그아웃(/me 안쪽)
 * 처럼 계정에 딸린 동작들이 서로 다른 곳에 흩어져 있었다. 그 셋을 아바타 한 번 클릭 아래로 모은다.
 *
 * 배치 — **portal + fixed**:
 *   레일(`.app-rightrail`)은 폭 64px + `overflow:hidden` 이라(접기 애니메이션이 내부를 잘라야
 *   한다) 그 안에 절대배치하면 팝오버가 통째로 잘린다. 그래서 body 로 portal 하고 아바타의
 *   실측 위치(getBoundingClientRect)를 기준으로 왼쪽에 띄운다. 화면 아래로 넘치면 위로 올린다.
 *
 * 상호작용 계약은 상태바 드롭다운들과 같게 맞춘다 — 바깥 클릭 닫기 · ESC · `useModalLeave(160)`.
 * 근무 상태 목록은 **같은 팝오버 안에서 2단(프로필 ↔ 근태)** 으로 전환한다. 팝오버 위에 또
 * 드롭다운을 겹치면 좁은 우측 경계에서 둘 다 갈 곳이 없다.
 */

interface Props {
  /** 앵커(레일 아바타). 팝오버는 이 요소의 왼쪽에 붙는다. */
  anchorRef: React.RefObject<HTMLElement>;
}

/** 팝오버 폭·여백(px) — CSS `.profile-pop` 과 동기 유지. */
const POP_WIDTH = 264;
const GAP = 10;
const EDGE = 12;

type View = 'profile' | 'status';

/**
 * 잠금 단축키 표기 — mac 은 `⌘L`, 그 밖은 `Ctrl+L`. **표시에만** 쓴다.
 *
 * 동작 쪽은 `ScreenLockHost` 가 `ctrlKey || metaKey` 로 두 키를 모두 받으므로(ScreenLock.tsx),
 * 이 판정이 틀려도 눌리기는 한다 — 틀렸을 때 잃는 것은 "표기 하나"뿐이다.
 *
 * 키캡은 **이 한 줄에만** 붙인다. 저장소에 단축키 레지스트리가 없고 전역 단축키는 잠금뿐이라,
 * 다른 줄에 지어 붙이면 눌러도 안 되는 키가 생긴다.
 */
function lockKeyCap(): string {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent || '';
  return /Mac|iPhone|iPad|iPod/i.test(ua) ? '\u2318L' : 'Ctrl+L';
}

interface MyInfo { name: string; position: string | null; email: string | null; avatarUrl: string | null }

/**
 * 소속·사번·휴가 잔여 (2026-08-31 · 시안 맞춤 9/9).
 *
 * ★ 셋 다 **없어도 되는 값**이다 — 못 받으면 그 줄·그 숫자만 빠지고 나머지는 그대로 산다.
 *   (인사 정보를 아직 안 채운 사람, 근태를 안 쓰는 회사가 있다.)
 */
interface MyExtra { org: string | null; employeeNumber: string | null; leaveRemaining: number | null }

/**
 * 팝오버가 뜰 때마다 프로필을 다시 부르지 않도록 모듈 캐시를 둔다(직책·이메일은 자주 바뀌지
 * 않는다). 프로필 저장은 `ProfileSection` 이 하고 authStore(name·avatarUrl)를 갱신하므로,
 * 표시에는 authStore 값을 우선 얹어 저장 직후에도 최신으로 보인다.
 */
let cachedInfo: MyInfo | null = null;
let cachedExtra: MyExtra | null = null;
export function _resetProfilePopoverCacheForTests(): void { cachedInfo = null; cachedExtra = null; }
/**
 * ★★★ **로그아웃하면 비운다** (2026-08-31 · 사용자 실측).
 *    「테스트팀장 → 로그아웃 → 테스트 사원 로그인」에서 **이사 · 테스트**가 그대로 남았다.
 *    이름은 `authStore` 에서 새로 오는데 **직위·소속·사번·연차는 이 두 변수**에서 오기 때문에,
 *    한 화면에 두 사람이 섞여 보였다(이름이 맞아서 더 안 들켰다).
 */
registerSessionCache(() => { cachedInfo = null; cachedExtra = null; });

export function ProfilePopover({ anchorRef }: Props): JSX.Element | null {
  useSyncExternalStore(subscribeLayoutLang, getLayoutLang, getLayoutLang);
  const open = useProfileUiStore((s) => s.popoverOpen);
  const close = useProfileUiStore((s) => s.closePopover);
  const openSettings = useProfileUiStore((s) => s.openSettings);
  const openSignature = useProfileUiStore((s) => s.openSignature);

  const userId = useAuthStore((s) => s.user?.id ?? null);
  const authName = useAuthStore((s) => s.user?.name ?? '');
  const authEmail = useAuthStore((s) => s.user?.email ?? '');
  const authAvatar = useAuthStore((s) => s.user?.avatarUrl ?? null);

  const status = useWorkStatusStore((s) => s.status);
  const pending = useWorkStatusStore((s) => s.pending);
  const error = useWorkStatusStore((s) => s.error);
  const select = useWorkStatusStore((s) => s.select);
  const today = useWorkStatusStore((s) => s.today);
  const hydrated = useWorkStatusStore((s) => s.hydrated);
  // 오늘이 간주 출근일(승인된 출장·외근)이면 퇴근이 열린다 — 근태 D2, 2026-08-24.
  const deemed = useWorkStatusStore((s) => s.deemed);

  // 화면잠금 — 켜져 있어야 `lock()` 이 화면을 덮는다(ScreenLockHost). 꺼져 있으면 설정으로 보낸다.
  const lockEnabled = useScreenLockStore((s) => s.settings.enabled);
  const lockNow = useScreenLockStore((s) => s.lock);

  const navigate = useNavigate();
  const { rows: menuRows } = useMenuConfig();
  const [info, setInfo] = useState<MyInfo | null>(cachedInfo);
  const [extra, setExtra] = useState<MyExtra | null>(cachedExtra);
  const [view, setView] = useState<View>('profile');
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const { shouldRender, isLeaving } = useModalLeave(open, 160);

  useEscapeClose(close, !open);

  // 열릴 때마다 프로필 화면(2단)은 처음부터 — 지난번에 근태 목록을 보다 닫았다고 해서
  // 다음에 열 때 그 화면이 나오면 "내 프로필" 을 눌렀는데 다른 게 뜬 것처럼 보인다.
  useEffect(() => { if (open) setView('profile'); }, [open]);

  // 신상 조회 — 최초 1회(캐시). 실패는 조용히 무시하고 authStore 값으로 표시한다.
  useEffect(() => {
    if (!open || cachedInfo) return;
    let cancelled = false;
    getMyProfile()
      .then((p) => {
        if (cancelled) return;
        cachedInfo = {
          name: p.name,
          position: p.position ?? null,
          email: p.email ?? null,
          avatarUrl: p.avatarUrl ?? null,
        };
        setInfo(cachedInfo);
      })
      .catch(() => { /* best-effort */ });
    return () => { cancelled = true; };
  }, [open]);

  /**
   * 소속 · 사번 · 휴가 잔여 — **최초 1회**(캐시). 셋 다 실패해도 팝오버는 그대로 뜬다.
   *
   * ★ `fetchPersonCard(나)` 는 SELF 범위라 전부 오고 **열람 기록도 안 남는다**(서버 표 참조).
   * ★ 휴가는 「열지 않고도 보이게」 하려는 숫자다 — 못 받으면 배지만 빠지고 줄은 남는다.
   *   ⚠ **결재 건수는 안 붙인다** — 전역 카운트 라우트가 없어 팝오버를 열 때마다 목록을
   *     두 번 불러야 한다. 숫자 하나 때문에 그 값은 비싸고, **모르는 수를 지어 적지 않는다**.
   */
  useEffect(() => {
    if (!open || cachedExtra || userId === null) return;
    let cancelled = false;
    const id = String(userId);
    void Promise.allSettled([fetchPersonCard(id), getLeaveSummary()]).then(([card, leave]) => {
      if (cancelled) return;
      const p = card.status === 'fulfilled' ? card.value.person : null;
      const org = p
        ? [p.divisionName, p.teamName].filter(Boolean).join(' ') || null
        : null;
      cachedExtra = {
        org,
        employeeNumber: p?.employeeNumber ?? null,
        // 판 F — 화면이 보여 주던 것은 «낼 수 있는 날»(잔여 − 신청중)이다.
        leaveRemaining: leave.status === 'fulfilled' ? leaveAvailable(leave.value.annual) : null,
      };
      setExtra(cachedExtra);
    });
    return () => { cancelled = true; };
  }, [open, userId]);

  // 앵커 기준 위치 — 열리는 순간 + 리사이즈/스크롤 시 재계산.
  const place = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const h = panelRef.current?.offsetHeight ?? 260;
    const left = Math.max(EDGE, r.left - POP_WIDTH - GAP);
    const top = Math.min(Math.max(EDGE, r.top), Math.max(EDGE, window.innerHeight - h - EDGE));
    setPos({ top, left });
  }, [anchorRef]);

  useLayoutEffect(() => { if (shouldRender) place(); }, [shouldRender, view, place]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place]);

  // 바깥 클릭 닫기 — 앵커는 제외한다(앵커 클릭은 토글이라 여기서 닫으면 곧바로 다시 열려 깜빡인다).
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t)) return;
      if (anchorRef.current?.contains(t)) return;
      close();
    };
    const timer = setTimeout(() => document.addEventListener('mousedown', onPointer), 0);
    return () => { clearTimeout(timer); document.removeEventListener('mousedown', onPointer); };
  }, [open, close, anchorRef]);

  if (!shouldRender || userId === null) return null;

  const name = authName || info?.name || '';
  const position = info?.position ?? null;
  const email = authEmail || info?.email || '';
  const avatarUrl = authAvatar ?? info?.avatarUrl ?? null;
  const currentLabel = status ? statusLabel(status) : tLayout('layout.workstatus.none');
  // 오늘 출근 기록이 없으면 퇴근을 못 고른다 — 서버 규칙(NOT_CHECKED_IN)을 화면이 먼저 말한다.
  const offLocked = isCheckOutLocked({ hydrated, checkIn: today.checkIn, deemed: Boolean(deemed) });
  const lockLabel = lockEnabled ? tLayout('layout.profile.lock') : tLayout('layout.profile.lock_off');

  const pick = (key: typeof WORK_STATUS_KEYS[number]) => {
    void select(key).then(() => {
      // 서버 왕복(출근/퇴근)이 실패했으면 사유를 보여줘야 하므로 목록에 머문다.
      if (!useWorkStatusStore.getState().error) setView('profile');
    });
  };

  /**
   * 잠금 — 로그아웃 **바로 위**. 되돌릴 수 있는 동작이라 위험색을 쓰지 않는다.
   * 새 상태를 만들지 않는다 — 유휴 만료 · 단축키와 **같은 `lock()`** 하나를 부른다.
   */
  const onSignature = () => { openSignature(); };

  const onLock = () => {
    close();
    if (lockEnabled) lockNow();
    else openSettings('security', 'screen-lock');
  };

  const onLogout = () => {
    close();
    void logout(tLayout('layout.profile.logout_confirm'));
  };

  /**
   * ★★★ **「내 것」으로 가는 길이 없었다** (2026-08-31 · 시안 맞춤 9/9).
   *
   *    팝오버는 세 줄(근무 상태 · 설정 · 로그아웃)뿐이라, 내 인사 정보·근태·휴가·결재는
   *    **메뉴를 알고 트리를 펼칠 줄 아는 사람만** 갔다. 시안이 여기 네 줄을 놓은 이유가 그것이다 —
   *    아바타 → 한 번이면 닿는다.
   *
   * ★ **회사가 끈 메뉴로는 길을 내지 않는다.** 눌러도 404(MENU_DISABLED)가 뜨는 줄을 세우면
   *   그게 잘못된 안내다. 레지스트리를 못 읽으면 종전대로 보인다(fail-open — `isMenuVisible`).
   * ★ 화면은 **이미 있는 것**이다 — 새 화면을 만들지 않았고, 주소도 메뉴가 쓰는 상수를 그대로 쓴다.
   */
  const goto = (to: string) => { close(); navigate(to); };
  const myLinks: Array<{ key: string; menuKey: string; to: string; label: string; icon: typeof Clock; badge?: string }> = [
    { key: 'record', menuKey: 'hr', to: `${HR_HOME_PATH}/me/record`, label: tLayout('layout.profile.my_hr'), icon: BookMarked },
    { key: 'att', menuKey: 'attendance', to: ATT_HOME_PATH, label: tLayout('layout.profile.my_attendance'), icon: Clock },
    {
      key: 'leave', menuKey: 'attendance', to: `${ATT_HOME_PATH}/me/leave`,
      label: tLayout('layout.profile.my_leave'), icon: CalendarDays,
      // 「열지 않고도 보이는」 숫자. 못 받았으면 배지가 없다 — 0 으로 적지 않는다.
      ...(extra?.leaveRemaining != null
        ? { badge: tLayout('layout.profile.days', { n: String(extra.leaveRemaining) }) }
        : {}),
    },
    { key: 'approval', menuKey: 'approval', to: '/approval', label: tLayout('layout.profile.my_approval'), icon: FileCheck },
  ].filter((x) => isMenuVisible(menuRows, x.menuKey));

  return createPortal(
    <div
      ref={panelRef}
      className={`profile-pop${isLeaving ? ' profile-pop--leaving' : ''}`}
      style={pos ? { top: pos.top, left: pos.left } : { visibility: 'hidden' }}
      role="dialog"
      aria-label={tLayout('layout.profile.aria')}
      aria-hidden={isLeaving || undefined}
    >
      {view === 'profile' ? (
        <>
          <div className="profile-pop__head">
            <MyStatusAvatar userId={userId} name={name} src={avatarUrl} size="lg" />
            <div className="profile-pop__meta">
              <div className="profile-pop__name">{name}</div>
              {/* ★ 직위 옆에 **소속**을 붙인다 — 「내가 어디 소속인가」를 팝오버가 답한다. */}
              <div className="profile-pop__position">
                {position || tLayout('layout.profile.no_position')}
                {extra?.org && <span className="profile-pop__org"> · {extra.org}</span>}
              </div>
              {/* 사번 — 있을 때만. 없는 사람(인사 정보 미등록)에게 빈 줄을 남기지 않는다. */}
              {extra?.employeeNumber && (
                <div className="profile-pop__empno">
                  {tLayout('layout.profile.employee_no', { no: extra.employeeNumber })}
                </div>
              )}
              {email && (
                <div className="profile-pop__email" title={email}>
                  <Mail size={12} aria-hidden />
                  <span>{email}</span>
                </div>
              )}
            </div>
          </div>

          <div className="profile-pop__actions">
            <button
              type="button"
              className="profile-pop__row"
              onClick={() => setView('status')}
              aria-label={tLayout('layout.profile.attendance')}
            >
              <span className="profile-pop__rowico" aria-hidden><Clock size={15} /></span>
              <span className="profile-pop__rowlabel">{tLayout('layout.profile.attendance')}</span>
              <span className={`profile-pop__rowvalue is-${status ? WORK_STATUS_TONE[status] : 'none'}`}>
                {currentLabel}
              </span>
              <ChevronRight size={14} aria-hidden className="profile-pop__chev" />
            </button>

            {myLinks.length > 0 && (
              <>
                <div className="profile-pop__sep" role="separator" />
                {myLinks.map((l) => {
                  const Icon = l.icon;
                  return (
                    <button
                      key={l.key}
                      type="button"
                      className="profile-pop__row"
                      onClick={() => goto(l.to)}
                      aria-label={l.label}
                    >
                      <span className="profile-pop__rowico" aria-hidden><Icon size={15} /></span>
                      <span className="profile-pop__rowlabel">{l.label}</span>
                      {l.badge && <span className="profile-pop__rowvalue is-none">{l.badge}</span>}
                      <ChevronRight size={14} aria-hidden className="profile-pop__chev" />
                    </button>
                  );
                })}
                <div className="profile-pop__sep" role="separator" />
              </>
            )}

            <button
              type="button"
              className="profile-pop__row"
              onClick={() => openSettings()}
              aria-label={tLayout('layout.profile.settings')}
            >
              <span className="profile-pop__rowico" aria-hidden><Settings size={15} /></span>
              <span className="profile-pop__rowlabel">{tLayout('layout.profile.settings')}</span>
            </button>

            {/*
              ★★ **내 서명** (2026-08-29 · 판 8 잔여 ②) — 결재 시트에서만 만들 수 있던 것을
                 여기서도 만든다. 시트의 입구는 그대로 둔다(결재하려다 딴 데로 보내지 않는다는
                 판 8 의 이유는 여전히 옳다) — 다만 **결재할 일이 없을 때 미리 만들려는 사람**
                 에게는 그 입구가 없는 것과 같았다.
              ★ 여는 것은 **같은 모달**이다 — 두 벌로 만들면 한쪽만 고쳐진다.
            */}
            <button
              type="button"
              className="profile-pop__row"
              onClick={onSignature}
              aria-label={tLayout('layout.profile.signature')}
            >
              <span className="profile-pop__rowico" aria-hidden><PenLine size={15} /></span>
              <span className="profile-pop__rowlabel">{tLayout('layout.profile.signature')}</span>
              <ChevronRight size={14} aria-hidden className="profile-pop__chev" />
            </button>

            <button
              type="button"
              className="profile-pop__row profile-pop__row--lock"
              onClick={onLock}
              aria-label={lockLabel}
            >
              <span className="profile-pop__rowico" aria-hidden><Lock size={15} /></span>
              <span className="profile-pop__rowlabel">{lockLabel}</span>
              {lockEnabled && <kbd className="profile-pop__key">{lockKeyCap()}</kbd>}
            </button>

            <button
              type="button"
              className="profile-pop__row profile-pop__row--danger"
              onClick={onLogout}
              aria-label={tLayout('layout.profile.logout')}
            >
              <span className="profile-pop__rowico" aria-hidden><LogOut size={15} /></span>
              <span className="profile-pop__rowlabel">{tLayout('layout.profile.logout')}</span>
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="profile-pop__subhead">
            <button
              type="button"
              className="profile-pop__back"
              onClick={() => setView('profile')}
              aria-label={tLayout('layout.profile.back')}
            >
              <ChevronLeft size={15} aria-hidden />
            </button>
            <h3 className="profile-pop__title">{tLayout('layout.workstatus.title')}</h3>
          </div>

          <div className="profile-pop__list" role="listbox" aria-label={tLayout('layout.workstatus.title')}>
            {WORK_STATUS_KEYS.map((key) => {
              const Icon = ICON_BY_STATUS[key];
              const selected = status === key;
              const busy = pending === key;
              // 퇴근은 출근한 사람만 — 상태바 드롭다운과 같은 판정을 쓴다(2026-08-23).
              const locked = key === 'off' && offLocked;
              const desc = locked
                ? tLayout('layout.workstatus.locked_off')
                : isServerBacked(key)
                  ? tLayout(`layout.workstatus.desc.${key}`)
                  : tLayout('layout.workstatus.desc.local');
              return (
                <button
                  key={key}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`profile-pop__status is-${WORK_STATUS_TONE[key]}${selected ? ' is-selected' : ''}`}
                  disabled={pending !== null || locked}
                  onClick={() => pick(key)}
                >
                  <span className="profile-pop__rowico" aria-hidden><Icon size={15} /></span>
                  <span className="profile-pop__statusbody">
                    <span className="profile-pop__statustitle">{statusLabel(key)}</span>
                    <span className="profile-pop__statusdesc">
                      {busy ? tLayout('layout.workstatus.saving') : desc}
                    </span>
                  </span>
                  {selected && <Check size={15} aria-hidden className="profile-pop__check" />}
                </button>
              );
            })}
          </div>

          {error && <p className="profile-pop__error" role="alert">{error}</p>}
        </>
      )}
    </div>,
    document.body,
  );
}
